import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  CircularProgress,
  Container,
  Grid,
  Divider,
  Paper,
  IconButton,
  useTheme
} from "@mui/material";
import {
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  People as PeopleIcon,
  AccessTime as TimeIcon,
  PlayCircle as PlayIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  LocalOffer as OfferIcon,
  Timer as TimerIcon
} from "@mui/icons-material";
import { axiosInstance } from "../axiosInstance.jsx";
import GoogleAd from "./GoogleAd.jsx";
import PopupAd from "../components/PopupAd.jsx";


export function CourseDetailPage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [couponExpired, setCouponExpired] = useState(false);
  const theme = useTheme();
  
  useEffect(() => {
    async function fetchCourse() {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/courses/${slug}`);
        setCourse(res.data);
      } catch (err) {
        setCourse(null);
      }
      setLoading(false);
    }
    fetchCourse();
  }, [slug]);

  // Calculate coupon expiration
  const calculateTimeRemaining = useCallback(() => {
    if (!course) return;
    
    const createdDate = new Date(course.created_at);
    const expiryDate = new Date(createdDate);
    expiryDate.setDate(expiryDate.getDate() + 5);
    const now = new Date();
    
    const diff = expiryDate - now;
    
    if (diff <= 0) {
      setCouponExpired(true);
      return;
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    setTimeRemaining({ days, hours, minutes, seconds });
    setCouponExpired(false);
  }, [course]);

  useEffect(() => {
    if (!course) return;
    
    calculateTimeRemaining();
    const timer = setInterval(calculateTimeRemaining, 1000);
    
    return () => clearInterval(timer);
  }, [course, calculateTimeRemaining]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!course) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <SchoolIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Course Not Found
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We couldn't find the course you're looking for. Please check the URL and try again.
        </Typography>
        <Button variant="contained" href="/" sx={{ mt: 2 }}>
          Browse All Courses
        </Button>
      </Container>
    );
  }

  // Calculate discount percentage
  const discountPercentage = course.sale_price > 0 
    ? Math.round(((course.price - course.sale_price) / course.price) * 100)
    : 0;

  // Format price
  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: price % 1 !== 0 ? 2 : 0
    });
  };

  // Format time unit for countdown
  const formatTimeUnit = (unit) => {
    return unit.toString().padStart(2, '0');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* <PopupAd /> */}
         {/* Space for Google Auto Ads */}
         <Box >
        {/* Google ads will be automatically inserted here */}
        <GoogleAd />
      </Box>
      {/* Hero Section */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Chip 
              label={course.category} 
              color="primary" 
              size="small" 
              sx={{ fontWeight: 600 }} 
            />
            <Chip 
              label={course.subcategory} 
              variant="outlined" 
              size="small" 
              sx={{ fontWeight: 500 }} 
            />
          </Stack>
          
          <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
            {course.name}
          </Typography>

          
   
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 800 }}>
            Master the skills you need to advance your career with this comprehensive course
          </Typography>
          
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {course.views || 0} students
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TimeIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Updated: {new Date(course.created_at).toLocaleDateString()}
              </Typography>
            </Stack>
          </Stack>
          
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <SchoolIcon fontSize="large" color="primary" />
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                Offered on {course.store || "Udemy"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Platform for professional development
              </Typography>
            </Box>
          </Stack>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ 
              backgroundImage: `url(${course.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: 200,
              position: 'relative',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7))'
              }
            }}>
              <Box sx={{ 
                position: 'absolute', 
                bottom: 16, 
                left: 16,
                color: 'white'
              }}>
                {discountPercentage > 0 && !couponExpired && (
                  <Chip 
                    label={`${discountPercentage}% OFF`} 
                    color="error" 
                    size="small"
                    sx={{ fontWeight: 700, mb: 1 }}
                  />
                )}
                
                <Typography variant="h5" fontWeight="bold">
                  {couponExpired ? 
                    formatPrice(course.price) : 
                    (course.sale_price >= 0 ? formatPrice(course.sale_price) : formatPrice(course.price))
                  }
                </Typography>
                
                {!couponExpired && course.price > 0 && course.sale_price !== course.price && (
                  <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                    {formatPrice(course.price)}
                  </Typography>
                )}
                
                {timeRemaining && !couponExpired && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mt: 1,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 1,
                    p: '4px 8px',
                    width: 'fit-content'
                  }}>
                    <TimerIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="caption">
                      {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m left
                    </Typography>
                  </Box>
                )}
                
                {couponExpired && (
                  <Chip 
                    label="Coupon expired" 
                    color="error" 
                    size="small"
                    sx={{ mt: 1, fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Button
                href={course.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                color={couponExpired ? "primary" : "success"}
                fullWidth
                size="large"
                sx={{ 
                  mb: 2, 
                  py: 1.5, 
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  boxShadow: couponExpired ? 
                    '0 4px 12px rgba(25, 118, 210, 0.4)' : 
                    '0 4px 12px rgba(46, 125, 50, 0.4)',
                  backgroundColor: couponExpired ? '' : '#2e7d32'
                }}
              >
                {couponExpired ? "Enroll Now" : "Get Discount Now"}
              </Button>
              
              <Stack direction="row" spacing={1} justifyContent="center">
                <IconButton aria-label="share" color="primary">
                  <ShareIcon />
                </IconButton>
                <IconButton aria-label="save" color="primary">
                  <BookmarkIcon />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Coupon Countdown Banner */}
      {!couponExpired && timeRemaining && (
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 6, 
          borderRadius: 3, 
          background: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.dark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)'
          }
        }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                ⏰ Limited Time Offer!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, maxWidth: 600 }}>
                Your exclusive discount expires soon. Enroll now to save {discountPercentage}% on this course!
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                backgroundColor: 'rgba(0,0,0,0.2)', 
                borderRadius: 2,
                p: 3,
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Offer Expires In:
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">
                      {formatTimeUnit(timeRemaining.days)}
                    </Typography>
                    <Typography variant="caption">Days</Typography>
                  </Box>
                  <Typography variant="h4">:</Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">
                      {formatTimeUnit(timeRemaining.hours)}
                    </Typography>
                    <Typography variant="caption">Hours</Typography>
                  </Box>
                  <Typography variant="h4">:</Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">
                      {formatTimeUnit(timeRemaining.minutes)}
                    </Typography>
                    <Typography variant="caption">Minutes</Typography>
                  </Box>
                  <Typography variant="h4">:</Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">
                      {formatTimeUnit(timeRemaining.seconds)}
                    </Typography>
                    <Typography variant="caption">Seconds</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      
      {/* Course Description */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 6, 
        borderRadius: 3, 
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Course Description
        </Typography>
        
        <Box 
          dangerouslySetInnerHTML={{ __html: course.description }} 
          sx={{ 
            '& p': { mb: 2, lineHeight: 1.8 },
            '& ul': { pl: 3, mb: 2 },
            '& li': { mb: 1, lineHeight: 1.8 }
          }}
        />
      </Paper>
      
      {/* Key Features */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            What You'll Learn
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
                <CheckIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography fontWeight="600" sx={{ mb: 0.5 }}>Comprehensive Coverage</Typography>
                  <Typography variant="body2" color="text.secondary">
                    All exam topics thoroughly covered
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
                <CheckIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography fontWeight="600" sx={{ mb: 0.5 }}>Hands-on Practice</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Practical exercises and real-world examples
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
                <CheckIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography fontWeight="600" sx={{ mb: 0.5 }}>Up-to-date Content</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aligned with the latest exam version
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
                <CheckIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography fontWeight="600" sx={{ mb: 0.5 }}>Exam Preparation</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Strategies to pass your certification
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Requirements
          </Typography>
          
          <Stack spacing={2}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <CheckIcon color="primary" sx={{ mt: 0.5 }} />
              <Box>
                <Typography fontWeight="600" sx={{ mb: 0.5 }}>Basic Knowledge</Typography>
                <Typography variant="body2" color="text.secondary">
                  Understanding of networking concepts
                </Typography>
              </Box>
            </Stack>
            
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <CheckIcon color="primary" sx={{ mt: 0.5 }} />
              <Box>
                <Typography fontWeight="600" sx={{ mb: 0.5 }}>Computer Access</Typography>
                <Typography variant="body2" color="text.secondary">
                  Computer with internet connection
                </Typography>
              </Box>
            </Stack>
            
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <CheckIcon color="primary" sx={{ mt: 0.5 }} />
              <Box>
                <Typography fontWeight="600" sx={{ mb: 0.5 }}>Willingness to Learn</Typography>
                <Typography variant="body2" color="text.secondary">
                  Dedication to complete the course
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
      
      {/* Pricing & Enrollment */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 6, 
        borderRadius: 3, 
        backgroundColor: couponExpired ? 'primary.light' : 'success.light', 
        backgroundImage: couponExpired ? 
          'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0.05) 100%)' :
          'linear-gradient(135deg, rgba(46,125,50,0.1) 0%, rgba(46,125,50,0.05) 100%)',
        border: '1px solid',
        borderColor: couponExpired ? 'primary.main' : '#2e7d32',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: couponExpired ? 
            'radial-gradient(circle, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0) 70%)' :
            'radial-gradient(circle, rgba(46,125,50,0.1) 0%, rgba(46,125,50,0) 70%)'
        }
      }}>
        <Grid container alignItems="center" spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {couponExpired ? "Start Learning Today" : "Limited Time Offer!"}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, maxWidth: 600 }}>
              {couponExpired 
                ? "Join thousands of students who have transformed their careers with this certification preparation"
                : `Enroll now to save ${discountPercentage}% on this comprehensive certification course!`}
            </Typography>
            
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3 }}>
              <OfferIcon color={couponExpired ? "primary" : "success"} fontSize="large" />
              <Box>
                <Typography variant="body1" fontWeight="600">
                  {couponExpired 
                    ? 'Special Learning Opportunity' 
                    : `${discountPercentage}% Discount Applied`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {couponExpired ? 'Start your learning journey today' : 'Limited time offer'}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              backgroundColor: 'white', 
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {couponExpired ? 'Course Price' : 'Discounted Price'}
              </Typography>
              
              {!couponExpired && course.sale_price >= 0 ? (
                <>
                  <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ mb: 1 }}>
                    {formatPrice(course.sale_price)}
                  </Typography>
                  {course.price > 0 && course.sale_price !== course.price && (
                    <Typography variant="body1" sx={{ textDecoration: 'line-through', mb: 2 }}>
                      {formatPrice(course.price)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="h3" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
                  {formatPrice(course.price)}
                </Typography>
              )}
              
              {!couponExpired && timeRemaining && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  p: 1
                }}>
                  <TimerIcon fontSize="small" color="warning" sx={{ mr: 1 }} />
                  <Typography color="black" variant="body2">
                    Offer expires in: {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m
                  </Typography>
                </Box>
              )}
              
              <Button
                href={course.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                color={couponExpired ? "primary" : "success"}
                fullWidth
                size="large"
                sx={{ 
                  py: 1.5, 
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  boxShadow: couponExpired ? 
                    '0 4px 12px rgba(25, 118, 210, 0.4)' : 
                    '0 4px 12px rgba(46, 125, 50, 0.4)',
                  backgroundColor: couponExpired ? '' : '#2e7d32',
                  '&:hover': {
                    backgroundColor: couponExpired ? '' : '#1b5e20'
                  }
                }}
              >
                {couponExpired ? "Enroll Now" : "Claim Your Discount"}
              </Button>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                30-Day Money-Back Guarantee
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Course Details */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ 
            p: 3, 
            borderRadius: 3, 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            height: '100%'
          }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
              Certification Benefits
            </Typography>
            
            <Stack spacing={3}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box sx={{ 
                  backgroundColor: 'primary.main', 
                  borderRadius: '50%', 
                  width: 40, 
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <SchoolIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography fontWeight="600" sx={{ mb: 0.5 }}>Industry Recognition</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Validate your skills with a globally recognized certification
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box sx={{ 
                  backgroundColor: 'primary.main', 
                  borderRadius: '50%', 
                  width: 40, 
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <PeopleIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography fontWeight="600" sx={{ mb: 0.5 }}>Career Advancement</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Open doors to new job opportunities and higher salaries
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box sx={{ 
                  backgroundColor: 'primary.main', 
                  borderRadius: '50%', 
                  width: 40, 
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <PlayIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography fontWeight="600" sx={{ mb: 0.5 }}>Practical Skills</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gain hands-on experience with real-world networking scenarios
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ 
            p: 3, 
            borderRadius: 3, 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            height: '100%'
          }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
              Course Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Category</Typography>
                <Typography fontWeight="600">{course.category}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Subcategory</Typography>
                <Typography fontWeight="600">{course.subcategory}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Platform</Typography>
                <Typography fontWeight="600">{course.store || 'Udemy'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Created</Typography>
                <Typography fontWeight="600">
                  {new Date(course.created_at).toLocaleDateString()}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Students</Typography>
                <Typography fontWeight="600">{course.views || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Price</Typography>
                <Typography fontWeight="600">
                  {couponExpired || course.sale_price <= 0 ? 
                    formatPrice(course.price) : 
                    formatPrice(course.sale_price)}
                  {!couponExpired && course.sale_price > 0 && (
                    <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
                      {discountPercentage}% OFF
                    </Typography>
                  )}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Coupon Status</Typography>
                <Typography fontWeight="600">
                  {couponExpired ? 
                    <Chip label="Expired" color="error" size="small" /> : 
                    <Chip label="Active" color="success" size="small" />
                  }
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Expiration</Typography>
                <Typography fontWeight="600">
                  {couponExpired ? 
                    "Offer expired" : 
                    (timeRemaining ? 
                      `${timeRemaining.days} days left` : 
                      "Calculating...")
                  }
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}